import React, { useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { ClientSession, ClientSessionFilter } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import { PortalDataTable, PortalTableColumn } from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { ClientPrimaryTabs } from "../components/PortalNavigation";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";
import { usePortalData } from "../hooks/usePortalData";
import {
  navigateWithinPortal,
  withPortalContext,
} from "../routing/portalRoutes";
import { PortalLink } from "../components/PortalLink";
import { useSessionLocation } from "../hooks/useSessionLocation";
import {
  clientSessionPath,
  SESSION_PAGE_SIZE,
} from "../utils/clientSessionNavigation";
import { formatPortalDateTime } from "../utils/dateTime";

const PAGE_SIZE = SESSION_PAGE_SIZE;
const filters: { value: ClientSessionFilter; label: string }[] = [
  { value: "past-week", label: "Past Week" },
  { value: "all", label: "All Sessions" },
];
export function ClientSessionsPage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const workspace = useClientWorkspace();
  const { search, filter: sessionFilter, pageOffset } = useSessionLocation();
  const transcriptPath = useCallback(
    (sessionId: string) =>
      clientSessionPath(patientId, workspace.source, search, sessionId),
    [patientId, workspace.source, search],
  );
  const columns = useMemo<PortalTableColumn<ClientSession>[]>(
    () => [
      {
        key: "startedAt",
        header: "Start Time",
        initialWidth: 260,
        render: (row) => (
          <PortalLink
            className="cp-session-start-link"
            path={transcriptPath(row.sessionId)}
            clinicianCode={clinicianCode}
          >
            {formatPortalDateTime(row.startedAt)}
          </PortalLink>
        ),
      },
      {
        key: "lastMessageAt",
        header: "Last Message Time",
        initialWidth: 260,
        render: (row) => formatPortalDateTime(row.lastMessageAt),
      },
      {
        key: "messageCount",
        header: "Message Count",
        initialWidth: 150,
        render: (row) => row.messageCount.toLocaleString(),
      },
    ],
    [transcriptPath, clinicianCode],
  );
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getClientSessions(
        clinicianCode,
        patientId,
        sessionFilter,
        PAGE_SIZE,
        pageOffset,
        signal,
        workspace.accessScope,
      ),
    [
      clinicianCode,
      patientId,
      sessionFilter,
      pageOffset,
      workspace.accessScope,
    ],
  );
  const state = usePortalData(request);
  const response =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  // usePortalData retains old data during refresh. Never show it under a new filter/page/client.
  const data =
    response?.patient?.id === patientId &&
    response.sessionFilter === sessionFilter &&
    response.pageOffset === pageOffset &&
    response.pageSize === PAGE_SIZE &&
    response.authorized &&
    response.found
      ? response
      : undefined;
  const snapshot = getClinicianPortalClientSnapshot(
    patientId,
    workspace.accessScope,
  );
  const patient =
    data?.patient ?? (state.status !== "error" ? snapshot?.patient : undefined);
  const therapist =
    data?.therapist ??
    (state.status !== "error" ? snapshot?.therapist : undefined);
  const pending = state.status === "loading" || state.status === "refreshing";
  const changeView = (filter: ClientSessionFilter, offset: number) => {
    const next = new URLSearchParams(window.location.search);
    next.set("filter", filter);
    if (offset) next.set("pageOffset", String(offset));
    else next.delete("pageOffset");
    navigateWithinPortal(
      `${window.location.pathname}?${next.toString()}${window.location.hash}`,
    );
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Sessions",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="sessions"
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--wide cp-page-content--compact-client-list">
        <section aria-label="Sessions">
          <div className="cp-content-header cp-content-header--route-intro">
            <p>
              {sessionFilter === "past-week"
                ? "Conversations with a message in the past seven days."
                : "All recorded conversations with the assistant."}
            </p>
          </div>
          <div className="cp-list-toolbar cp-list-toolbar--homework cp-history-command-bar">
            <div
              className="cp-history-filters"
              role="group"
              aria-label="Session filter"
            >
              {filters.map((filter) => (
                <button
                  type="button"
                  key={filter.value}
                  aria-pressed={sessionFilter === filter.value}
                  onClick={() => {
                    if (sessionFilter !== filter.value)
                      changeView(filter.value, 0);
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cp-button cp-button--secondary"
              disabled={pending}
              onClick={state.refresh}
              aria-label={pending ? "Refreshing sessions" : "Refresh sessions"}
            >
              <RefreshCw aria-hidden="true" /> Refresh
            </button>
          </div>
          {pending && !data ? <LoadingState label="Loading sessions" /> : null}
          {state.status === "error" ? (
            <ErrorState error={state.error} onRetry={state.refresh} />
          ) : null}
          {state.status === "success" && !data ? (
            <ErrorState error={new Error("Sessions are unavailable")} />
          ) : null}
          {data ? (
            <div aria-busy={pending}>
              {data.sessions.length ? (
                <PortalDataTable
                  columns={columns}
                  records={data.sessions}
                  getRecordKey={(row) => row.sessionId}
                  caption="Client sessions, newest first"
                  onRecordOpen={(row) =>
                    navigateWithinPortal(
                      withPortalContext(transcriptPath(row.sessionId)),
                    )
                  }
                  getRecordOpenLabel={(row) =>
                    `Open transcript from ${formatPortalDateTime(row.startedAt)}`
                  }
                />
              ) : (
                <EmptyState
                  title="No sessions found"
                  description={
                    pageOffset > 0
                      ? "There are no sessions on this page. Return to the previous page."
                      : sessionFilter === "past-week"
                        ? "No conversations had messages in the past seven days. Try All Sessions."
                        : "This client has no recorded conversations."
                  }
                />
              )}
              <nav className="cp-pagination" aria-label="Sessions pagination">
                <span className="cp-pagination__summary" role="status">
                  {data.sessions.length
                    ? `${pageOffset + 1}–${pageOffset + data.sessions.length} sessions`
                    : "0 sessions"}
                </span>
                <div className="cp-pagination__controls">
                  <button
                    type="button"
                    className="cp-pagination__button"
                    aria-label="Previous page"
                    disabled={pending || pageOffset === 0}
                    onClick={() =>
                      changeView(
                        sessionFilter,
                        Math.max(0, pageOffset - PAGE_SIZE),
                      )
                    }
                  >
                    <ChevronLeft aria-hidden="true" />
                  </button>
                  <span className="cp-pagination__page-status">
                    Page {pageOffset / PAGE_SIZE + 1}
                  </span>
                  <button
                    type="button"
                    className="cp-pagination__button"
                    aria-label="Next page"
                    disabled={
                      pending || !data.hasMore || data.nextOffset === null
                    }
                    onClick={() => {
                      if (data.nextOffset !== null)
                        changeView(sessionFilter, data.nextOffset);
                    }}
                  >
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </nav>
            </div>
          ) : null}
        </section>
      </div>
    </ClinicianPortalLayout>
  );
}
